import { closeCreditHistoryModal } from 'dispatcher/modalDispatcher';
import { RootState } from 'reducer';
import React, { useCallback, useEffect, useState } from 'react';
import { Folder } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import api from 'entity/reward/rewardApi';
import Dialog from 'seedly-component-library/dialog';
import clsx from 'clsx';
import Button from 'seedly-component-library/button-new';
import { CreditHistory } from 'entity/reward/rewardTypes';
import useIsMobile from 'utils/hooks/useIsMobile';
import { Pagination } from 'entity/comment/commentTypes';
import CreditHistoryCard from './CreditHistoryCard';

interface FilterType {
  title: string;
  value: 'credit' | 'debit';
}
const CreditHistoryModal = () => {
  const [filter, setFilter] = useState<'credit' | 'debit'>('credit');
  const [history, setHistory] = useState<CreditHistory[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    per: 0,
    currentPage: 0,
    totalPage: 0,
    totalCount: 0,
  });

  const isCreditHistoryModalOpen = useSelector(
    (state: RootState) => state.modal.isCreditHistoryModalOpen,
  );
  const isMobile = useIsMobile();
  const dispatch = useDispatch();

  const filterList: FilterType[] = [
    {
      title: 'Credits Earned',
      value: 'credit',
    },
    {
      title: 'Credits Deducted',
      value: 'debit',
    },
  ];

  const fetchHistory = useCallback(
    async (page: number, state: 'credit' | 'debit') => {
      const response = await api.getCreditHistory({
        state,
        per: 10,
        page,
      });
      if (response.ok) {
        if (page === 1) {
          setHistory(response.data.data);
        } else {
          setHistory([...history, ...response.data.data]);
        }
        setPagination(response.data.meta.pagination);
      }
    },
    [history],
  );

  // if filter changed
  useEffect(() => {
    if (isCreditHistoryModalOpen) {
      setHistory([]);
      fetchHistory(1, filter);
    }
  }, [filter, isCreditHistoryModalOpen]);

  const historyList = history.map(item => {
    return (
      <CreditHistoryCard type={filter} creditHistory={item} key={item.id} />
    );
  });

  return (
    <Dialog
      isOpen={isCreditHistoryModalOpen}
      headerTitle="My Credit History"
      onCloseClick={() => dispatch(closeCreditHistoryModal())}
    >
      <div className="flex flex-col gap-5 w-full overflow-auto h-[85vh]">
        <div className="flex gap-5">
          {filterList.map(item => {
            const isSelected = item.value === filter;
            return (
              <span
                key={item.title}
                className={clsx(
                  'text-sm font-bold cursor-pointer',
                  isSelected ? 'text-blue-500' : 'text-neutral-500',
                )}
                onClick={() => {
                  setFilter(item.value);
                }}
                data-testid={`${item.value}-filter-button`}
              >
                {item.title}
              </span>
            );
          })}
        </div>
        <div className="flex flex-col w-full gap-3">
          <div className="list">
            {historyList.length === 0 ? (
              <div
                className={clsx(
                  'rounded center-in-div',
                  isMobile ? 'h-[80vh]' : 'h-[130px]',
                )}
              >
                <div className="flex flex-col items-center gap-3">
                  <Folder className="text-neutral-500" size={64} />
                  <div className="flex flex-col items-center gap-1">
                    <p className="text-base font-bold text-neutral-500">
                      No entry found
                    </p>
                    <p className="text-neutral-500 text-center">
                      {filter === 'credit'
                        ? 'After your first credit earn, you will be able to view it here'
                        : 'After your first credit deduction, you will be able to view it here'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              historyList
            )}
          </div>
          {pagination.currentPage < pagination.totalPage && (
            <div className="flex flex-col items-center">
              <Button
                variant="ghost"
                onClick={() => {
                  fetchHistory(pagination.currentPage + 1, filter);
                }}
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default CreditHistoryModal;
